import { PieChart } from "lucide-react";

const CATS = [
  ["housing", "Housing"], ["utilities", "Utilities"], ["food", "Food"], ["transportation", "Transport"],
  ["insurance", "Insurance"], ["healthcare", "Health"], ["debt", "Debt"], ["savings", "Savings"],
  ["entertainment", "Fun"], ["personal_care", "Personal"], ["children_pets", "Kids/Pets"], ["miscellaneous", "Misc"],
];
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CategoryInsights({ budget }) {
  const weeks = budget?.weeks || [];
  const totals = {};
  for (const [key, label] of CATS) {
    totals[key] = { label, amount: 0 };
  }
  let custom = 0;
  for (const w of weeks) {
    for (const [key] of CATS) totals[key].amount += Number(w.expenses?.[key]?.amount) || 0;
    custom += (w.custom || []).reduce((s, x) => s + (Number(x.amount) || 0), 0);
  }
  const rows = Object.values(totals);
  if (custom > 0) rows.push({ label: "Custom", amount: custom });
  rows.sort((a, b) => b.amount - a.amount);
  const grand = rows.reduce((s, r) => s + r.amount, 0);
  const max = Math.max(...rows.map((r) => r.amount), 1);
  const withSpend = rows.filter((r) => r.amount > 0);

  return (
    <section data-testid="snap-insights" className="bg-[#0d1730] border border-emerald-500/20 rounded-2xl p-5 sm:p-6 shadow-2xl shadow-emerald-950/40">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold tracking-wide text-emerald-300 flex items-center gap-2">
          <PieChart className="w-5 h-5" /> Where the money went
        </h2>
        <span className="text-xs text-slate-500">Total spend {money(grand)}</span>
      </div>

      {withSpend.length === 0 ? (
        <p className="text-sm text-slate-500 py-6 text-center">Add some expenses to see your spending breakdown.</p>
      ) : (
        <div className="space-y-2.5">
          {withSpend.map((r, i) => {
            const pct = grand ? Math.round((r.amount / grand) * 100) : 0;
            const w = Math.round((r.amount / max) * 100);
            const top = i === 0;
            return (
              <div key={r.label} data-testid={`snap-insight-${r.label}`} className="flex items-center gap-3">
                <span className="w-20 shrink-0 text-xs text-slate-400 text-right">{r.label}</span>
                <div className="flex-1 h-6 bg-white/[0.04] rounded-md overflow-hidden">
                  <div
                    className={`h-full rounded-md transition-all duration-500 ${top ? "bg-amber-400" : "bg-emerald-500/70"}`}
                    style={{ width: `${Math.max(w, 4)}%` }}
                    title={`${money(r.amount)} · ${pct}%`}
                  />
                </div>
                <span className="w-24 shrink-0 text-xs font-mono text-right text-slate-300">{money(r.amount)}<span className="text-slate-600"> · {pct}%</span></span>
              </div>
            );
          })}
        </div>
      )}
      {withSpend.length > 0 && (
        <p className="text-[11px] text-slate-500 mt-4">
          <span className="text-amber-400">■</span> Your biggest category this month is <span className="text-amber-300 font-medium">{withSpend[0].label}</span> at {money(withSpend[0].amount)}.
        </p>
      )}
    </section>
  );
}
